import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { logWeldNumberUpdated } from '../../../utils/productionAudit';

export interface WeldNumberAuditContext {
  projectId?: number | null;
  projectName?: string | null;
  productionWeldId?: number | string | null;
}

interface UpdateWeldNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  currentValue: string;
  onConfirm: (newValue: string, newJobNumber?: string) => void;
  loading?: boolean;
  label?: string;
  placeholder?: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  // Job number fields
  currentJobNumber?: string;
  jobNumberLabel?: string;
  jobNumberPlaceholder?: string;
  showJobNumber?: boolean;
  auditContext?: WeldNumberAuditContext;
}

export const UpdateWeldNumberDialog: React.FC<UpdateWeldNumberDialogProps> = ({
  open,
  onOpenChange,
  title = 'Update Weld Number and Job Number',
  currentValue,
  onConfirm,
  loading = false,
  label = 'Enter the new weld number:',
  placeholder = 'New Weld Number',
  description,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  maxWidth = '3xl',
  currentJobNumber = '',
  jobNumberLabel = 'Enter the new job number:',
  jobNumberPlaceholder = 'New Job Number',
  showJobNumber = false,
  auditContext,
}) => {
  const [newValue, setNewValue] = useState(currentValue);
  const [newJobNumber, setNewJobNumber] = useState(currentJobNumber);

  // Update local state when currentValue changes
  useEffect(() => {
    setNewValue(currentValue);
  }, [currentValue]);

  // Update local state when currentJobNumber changes
  useEffect(() => {
    setNewJobNumber(currentJobNumber);
  }, [currentJobNumber]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setNewValue(currentValue);
      setNewJobNumber(currentJobNumber);
    }
  }, [open, currentValue, currentJobNumber]);

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    if (newValue.trim() && !loading) {
      const trimmedWeldNumber = newValue.trim();
      const trimmedJobNumber = showJobNumber ? newJobNumber.trim() : undefined;

      if (auditContext) {
        logWeldNumberUpdated({
          projectId: auditContext.projectId,
          projectName: auditContext.projectName,
          productionWeldId: auditContext.productionWeldId,
          beforeWeldNumber: currentValue,
          afterWeldNumber: trimmedWeldNumber,
          beforeJobNumber: showJobNumber ? currentJobNumber : null,
          afterJobNumber: showJobNumber ? trimmedJobNumber ?? '' : null,
        });
      }

      onConfirm(trimmedWeldNumber, trimmedJobNumber);
    }
  };

  const maxWidthClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl'
  }[maxWidth];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`bg-white ${maxWidthClass} w-full`}>
        <DialogHeader className="pl-0 pr-14 pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {title}{showJobNumber && ' and Job Number'}
            </DialogTitle>
            <Button
              onClick={handleConfirm}
              disabled={!newValue.trim() || loading}
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                confirmButtonText
              )}
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          {description ? (
            <p className="text-[13px] text-text-grey" dangerouslySetInnerHTML={{ __html: description }} />
          ) : (
            <p className="text-[13px] text-text-grey">
              Do you want to modify the weld number <strong className="text-blueprint-blue">{currentValue}</strong>
              {showJobNumber && (
                <>
                  {' '}and job number <strong className="text-blueprint-blue">{currentJobNumber}</strong>
                </>
              )}
              ?
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newWeldNumber" className="text-[12px] font-medium text-text-grey">
                {label}
              </Label>
              <Input
                id="newWeldNumber"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={placeholder}
                className="bg-white border-[#E2E5EA] text-text-grey placeholder:text-heritage-grey"
                disabled={loading}
                autoFocus
              />
            </div>
            {showJobNumber && (
              <div className="space-y-2">
                <Label htmlFor="newJobNumber" className="text-[12px] font-medium text-text-grey">
                  {jobNumberLabel}
                </Label>
                <Input
                  id="newJobNumber"
                  value={newJobNumber}
                  onChange={(e) => setNewJobNumber(e.target.value)}
                  placeholder={jobNumberPlaceholder}
                  className="bg-white border-[#E2E5EA] text-text-grey placeholder:text-heritage-grey"
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

